import { ChangeDetectionStrategy, Component, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CustomerAccountSex,
  CustomerProfileCreatePayloadI,
  EthnicityI,
  GenderI,
  RacesI,
} from '@bodyanalytics/data-models-ui';
import { ethnicityData, genderData, raceData } from '@bodyanalytics/ui-core';

/**
 * Create-profile form (#1760 onboarding). Collects the profile fields the
 * backend `CreateSelfProfileDto` accepts. There is deliberately NO email field:
 * the caller is already authenticated and the backend derives the identity
 * email from the JWT (a client-supplied one is rejected with 400).
 */
@Component({
  selector: 'bodyanalytics-register-profile-form',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterProfileFormComponent {
  submitFormEvent = output<CustomerProfileCreatePayloadI>();

  readonly ethnicityList: EthnicityI[] = ethnicityData;
  readonly genderList: GenderI[] = genderData;
  readonly raceList: RacesI[] = raceData;

  formsubmit = false;

  public securityForm = new FormGroup({
    firstName: new FormControl('', [Validators.required, Validators.pattern('[a-zA-Z0-9]+')]),
    lastName: new FormControl('', [Validators.required, Validators.pattern('[a-zA-Z0-9]+')]),
    // Lenient by design: 7–20 digits with optional +, (), spaces, dashes. The
    // old `[0-9 ]{11}` pattern rejected every 10-digit US number, which would
    // have re-created the onboarding dead-end the moment this form was mounted.
    phoneNumber: new FormControl('', [Validators.required, Validators.pattern('[-+() 0-9]{7,20}')]),
    gender: new FormControl('', [Validators.required]),
    ethnicity: new FormControl('', [Validators.required]),
    race: new FormControl('', [Validators.required]),
    birthDate: new FormControl('', [Validators.required]),
  });

  /**
   * Validates and emits the typed create-profile payload. The gender selection
   * (stored as the option `value`: M/F/O/R) is mapped onto the backend's `sex`
   * domain, while its display name is forwarded as the free-text `gender` field.
   */
  public createProfile(): void {
    this.formsubmit = true;
    if (this.securityForm.invalid) {
      return;
    }
    const genderValue = this.securityForm.value.gender as string;
    this.submitFormEvent.emit({
      firstName: this.securityForm.value.firstName as string,
      lastName: this.securityForm.value.lastName as string,
      sex: this.toSex(genderValue),
      gender: this.genderList.find(gender => gender.value === genderValue)?.name,
      ethnicity: this.securityForm.value.ethnicity as string,
      race: this.securityForm.value.race as string,
      birthDate: this.securityForm.value.birthDate as string,
      phoneNumber: this.securityForm.value.phoneNumber as string,
    });
  }

  /**
   * Returns form
   */
  get form() {
    return this.securityForm.controls;
  }

  /** Maps the gender option value (M/F/O/R) onto the backend `sex` union. */
  private toSex(genderValue: string): CustomerAccountSex {
    if (genderValue === 'M') {
      return 'MALE';
    }
    if (genderValue === 'F') {
      return 'FEMALE';
    }
    return 'PREFER_NOT_TO_SAY';
  }
}
